import React, { useState, useEffect, useRef } from 'react';
import { MainLayout } from './MainLayout';
import '../styles/page-base.css';
import { supabase } from '../lib/supabase_client';

interface HelpCategory {
  id: string;
  title: string;
  description: string | null;
  sections: HelpSection[];
}

interface HelpSection {
  id: string;
  title: string;
  description: string | null;
  articles: HelpArticle[];
}

interface HelpArticle {
  id: string;
  title: string;
  content: string;
}

interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
}

export default function LearnRapidTagger() {
  const [helpCategories, setHelpCategories] = useState<HelpCategory[]>([]);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const contentRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [copiedIds, setCopiedIds] = useState<Set<string>>(new Set());
  const [highlightedArticleId, setHighlightedArticleId] = useState<string | null>(null);

  // Prevent body scrolling when component mounts AND compensate for scrollbar width
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    // Calculate scrollbar width before hiding it
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // Hide scrollbar and compensate for width to prevent layout shift
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    
    return () => {
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, []);

  useEffect(() => {
    fetchHelpData();
  }, []);

  const fetchHelpData = async () => {
    try {
      setIsLoading(true);
      setError(null);
  
      // Step 1: Fetch all active categories ordered by display_order
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('help_categories')
        .select('id, title, description, display_order')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
  
      if (categoriesError) {
        throw categoriesError;
      }
  
      if (!categoriesData || categoriesData.length === 0) {
        setHelpCategories([]);
        return;
      }
  
      // Step 2: Fetch all active sections ordered by display_order
      const categoryIds = categoriesData.map(cat => cat.id);
      const { data: sectionsData, error: sectionsError } = await supabase
        .from('help_sections')
        .select('id, category_id, title, description, display_order')
        .eq('is_active', true)
        .in('category_id', categoryIds)
        .order('category_id', { ascending: true })
        .order('display_order', { ascending: true });
  
      if (sectionsError) {
        throw sectionsError;
      }
  
      // Step 3: Fetch all active articles ordered by display_order
      const sectionIds = sectionsData ? sectionsData.map(sec => sec.id) : [];
      
      let articlesData: any[] = [];
      if (sectionIds.length > 0) {
        const { data: articles, error: articlesError } = await supabase
          .from('help_articles')
          .select('id, section_id, title, content, display_order')
          .eq('is_active', true)
          .in('section_id', sectionIds)
          .order('section_id', { ascending: true })
          .order('display_order', { ascending: true });
  
        if (articlesError) {
          throw articlesError;
        }
        articlesData = articles || [];
      }
  
      // Step 4: Build the hierarchical structure
      const categories: HelpCategory[] = [];
  
      // Group sections by category_id
      const sectionsByCategory = new Map<string, any[]>();
      if (sectionsData) {
        sectionsData.forEach((section: any) => {
          if (!sectionsByCategory.has(section.category_id)) {
            sectionsByCategory.set(section.category_id, []);
          }
          sectionsByCategory.get(section.category_id)!.push(section);
        });
      }
  
      // Group articles by section_id
      const articlesBySection = new Map<string, any[]>();
      articlesData.forEach((article: any) => {
        if (!articlesBySection.has(article.section_id)) {
          articlesBySection.set(article.section_id, []);
        }
        articlesBySection.get(article.section_id)!.push(article);
      });
  
      // Build categories with their sections
      categoriesData.forEach((category: any) => {
        const categorySections = sectionsByCategory.get(category.id) || [];
        
        const sections: HelpSection[] = [];
        categorySections.forEach((section: any) => {
          const sectionArticles = articlesBySection.get(section.id) || [];
          
          // Transform articles
          const articles = sectionArticles.map((article: any) => ({
            id: article.id,
            title: article.title,
            content: article.content || '',
          }));
  
          // Only add section if it has articles
          if (articles.length > 0) {
            sections.push({
              id: section.id,
              title: section.title,
              description: section.description,
              articles: articles,
            });
          }
        });
  
        // Only add category if it has sections
        if (sections.length > 0) {
          categories.push({
            id: category.id,
            title: category.title,
            description: category.description,
            sections: sections,
          });
        }
      });
  
      setHelpCategories(categories);
  
      // Set initial selected section
      if (categories.length > 0 && categories[0].sections.length > 0) {
        setSelectedSection(categories[0].sections[0].id);
      }
    } catch (err: any) {
      console.error('Error fetching help data:', err);
      setError('Failed to load help content. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Find current section
  let currentSection: HelpSection | undefined;
  for (const category of helpCategories) {
    currentSection = category.sections.find(s => s.id === selectedSection);
    if (currentSection) break;
  }

  // Filter categories and sections based on search query
  const filteredCategories = helpCategories.map(category => ({
    ...category,
    sections: category.sections.map(section => ({
      ...section,
      articles: section.articles.filter(article =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(section => section.articles.length > 0)
  })).filter(category => category.sections.length > 0);

  const handleSectionClick = (sectionId: string) => {
    // Clear highlight when user clicks a section
    if (highlightedArticleId) {
      clearHighlight(highlightedArticleId);
      setHighlightedArticleId(null);
    }
    // Clear hash when clicking a section manually
    window.history.replaceState(null, '', window.location.pathname);
    setSelectedSection(sectionId);
  };

  // Helper function to clear highlight
  const clearHighlight = (articleId: string) => {
    const outerElement = document.getElementById(`article-${articleId}`);
    if (outerElement) {
      const innerBlock = outerElement.querySelector('.bg-white.rounded-lg.shadow-sm') as HTMLElement;
      const targetElement = innerBlock || outerElement;
      targetElement.classList.remove('highlight-target');
    }
  };

  // Helper function to highlight and scroll to article
  const highlightAndScrollToArticle = (articleId: string) => {
    // Clear previous highlight
    if (highlightedArticleId && highlightedArticleId !== articleId) {
      clearHighlight(highlightedArticleId);
    }
    
    const outerElement = document.getElementById(`article-${articleId}`);
    if (outerElement) {
      // Find the inner white block div (the one with bg-white class)
      const innerBlock = outerElement.querySelector('.bg-white.rounded-lg.shadow-sm') as HTMLElement;
      const targetElement = innerBlock || outerElement;
      
      // Scroll first
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Add highlight class with a small delay to ensure scroll happens first
      setTimeout(() => {
        targetElement.classList.add('highlight-target');
        setHighlightedArticleId(articleId); // Track the highlighted article
      }, 300);
    }
  };

  // Handle search
  const handleSearch = () => {
    if (searchQuery.trim()) {
      for (const category of filteredCategories) {
        for (const section of category.sections) {
          if (section.articles.length > 0) {
            setSelectedSection(section.id);
            return;
          }
        }
      }
    }
  };

  // Process HTML content to add IDs and copy buttons to headings
  const processContent = (html: string, articleId: string): string => {
    if (!html) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Only add IDs to headings, no copy buttons
    const headings = tempDiv.querySelectorAll('h1, h2, h3, h4');
    headings.forEach((heading, index) => {
      const text = heading.textContent || '';
      const id = `article-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${index}`;
      heading.id = id;
    });
    
    return tempDiv.innerHTML;
  };

  // Copy link to clipboard
  const copySectionLink = (sectionId: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${sectionId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIds(prev => {
        const newSet = new Set(prev);
        newSet.add(sectionId);
        return newSet;
      });
      setTimeout(() => {
        setCopiedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(sectionId);
          return newSet;
        });
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Handle hash navigation on page load and when hash changes
  useEffect(() => {
    if (!helpCategories.length) return;

    const hash = window.location.hash.substring(1);
    if (!hash) {
      // Clear highlight if hash is cleared
      if (highlightedArticleId) {
        clearHighlight(highlightedArticleId);
        setHighlightedArticleId(null);
      }
      return;
    }

    // Check if it's an article hash
    if (hash.startsWith('article-')) {
      const articleId = hash.replace('article-', '');
      
      // Find the section that contains this article
      for (const category of helpCategories) {
        for (const section of category.sections) {
          const article = section.articles.find(a => a.id === articleId);
          if (article) {
            // Select the section if not already selected
            if (selectedSection !== section.id) {
              setSelectedSection(section.id);
              // Wait longer for section to render before scrolling
              setTimeout(() => {
                highlightAndScrollToArticle(articleId);
              }, 500);
            } else {
              // Section already selected, just scroll and highlight
              setTimeout(() => {
                highlightAndScrollToArticle(articleId);
              }, 100);
            }
            return;
          }
        }
      }
    } 
    // Check if it's a section hash
    else if (hash.startsWith('section-')) {
      const sectionId = hash.replace('section-', '');
      
      // Clear highlight when navigating to section
      if (highlightedArticleId) {
        clearHighlight(highlightedArticleId);
        setHighlightedArticleId(null);
      }
      
      // Find and select the section
      for (const category of helpCategories) {
        const section = category.sections.find(s => s.id === sectionId);
        if (section) {
          if (selectedSection !== sectionId) {
            setSelectedSection(sectionId);
          }
          
          // Scroll to top of section content
          setTimeout(() => {
            const mainContent = document.querySelector('.flex-1.overflow-y-auto');
            if (mainContent) {
              mainContent.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 200);
          return;
        }
      }
    }
  }, [helpCategories]);

  // Handle hash changes when user navigates (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (!hash) {
        // Clear highlight if hash is cleared
        if (highlightedArticleId) {
          clearHighlight(highlightedArticleId);
          setHighlightedArticleId(null);
        }
        return;
      }

      if (hash.startsWith('article-')) {
        const articleId = hash.replace('article-', '');
        setTimeout(() => {
          highlightAndScrollToArticle(articleId);
        }, 100);
      } else if (hash.startsWith('section-')) {
        // Clear highlight when navigating to section
        if (highlightedArticleId) {
          clearHighlight(highlightedArticleId);
          setHighlightedArticleId(null);
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [highlightedArticleId, helpCategories]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="learnRT-container" style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '24px' }}>
          <div className="max-w-6xl mx-auto w-full flex flex-col" style={{ height: '100%', boxSizing: 'border-box', padding: 0 }}>
            <div className="page-header" style={{ flexShrink: 0 }}>
              <div className="page-header-left">
                <h1 className="page-title">Learn Rapid Tagger</h1>
              </div>
            </div>
            <div className="page-card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500">Loading help content...</div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="learnRT-container" style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '24px' }}>
          <div className="max-w-6xl mx-auto w-full flex flex-col" style={{ height: '100%', boxSizing: 'border-box', padding: 0 }}>
            <div className="page-header" style={{ flexShrink: 0 }}>
              <div className="page-header-left">
                <h1 className="page-title">Learn Rapid Tagger</h1>
              </div>
            </div>
            <div className="page-card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div className="flex items-center justify-center py-12">
                <div className="text-red-500">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="learnRT-container" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative', padding: '24px' }}>
        <div className="max-w-6xl mx-auto w-full flex flex-col" style={{ height: '100%', boxSizing: 'border-box', padding: 0 }}>
          {/* Header - Fixed */}
          <div className="page-header" style={{ flexShrink: 0 }}>
            <div className="page-header-left">
              <h1 className="page-title">
                Learn Rapid Tagger
              </h1>
            </div>
          </div>
          
          {/* Content Area - Scrollable */}
          <div className="page-card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="flex" style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              {/* Left Sidebar Navigation - Show Categories and Sections */}
              <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto" style={{ height: '100%' }}>
                <div className="p-3">
                  {helpCategories.length === 0 ? (
                    <div className="text-sm text-gray-500 py-4">No help content available</div>
                  ) : (
                    filteredCategories.map((category, categoryIndex) => (
                      <div key={category.id} className={categoryIndex > 0 ? "mt-2" : ""}>
                        {/* Category Title */}
                        <div className="font-semibold text-blue-600 text-sm mb-0.5 px-2 py-0.5">
                          {category.title}
                        </div>
                        {/* Sections under this category */}
                        {category.sections.map((section) => (
                          <button
                            key={section.id}
                            onClick={() => handleSectionClick(section.id)}
                            className={`w-full text-left text-sm px-3 py-1.5 rounded transition mb-0.5 ${
                              selectedSection === section.id
                                ? 'bg-blue-100 text-blue-700 font-medium border-l-2 border-blue-600'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-blue-700'
                            }`}
                          >
                            {section.title}
                          </button>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Main Content Area - Show All Articles in Selected Section */}
              <div className="flex-1 overflow-y-auto" style={{ height: '100%', background: '#FAFAFA' }}>
                {currentSection && currentSection.articles.length > 0 ? (() => {
                  const sectionId = currentSection.id; // Store ID to avoid TypeScript narrowing issues
                  return (
                    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 32px' }}>
                      {/* Section Title with Copy Button on Same Line */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <h1 className="text-3xl font-bold text-gray-900" style={{ fontSize: '28px', fontWeight: 700, color: '#111827', margin: 0, flex: 1 }}>
                          {currentSection.title}
                        </h1>
                      </div>
                      
                      {/* Section Description (italic) */}
                      {currentSection.description && (
                        <div className="text-gray-500 mb-6" style={{ fontSize: '15px', color: '#6B7280', marginBottom: '24px', fontStyle: 'italic' }}>
                          {currentSection.description}
                        </div>
                      )}
                      
                      {/* Table of Contents - Only if more than one article */}
                      {currentSection.articles.length > 1 && (
                        <div 
                          style={{ 
                            backgroundColor: '#EFF6FF',
                            borderRadius: '8px',
                            padding: '20px',
                            marginBottom: '32px',
                            border: '1px solid #DBEAFE'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', color: '#3B82F6' }}>
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1E40AF', margin: 0 }}>
                              Table of Contents
                            </h3>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {currentSection.articles.map((article, index) => (
                              <a
                                key={article.id}
                                href={`#article-${article.id}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  // Clear previous highlight
                                  if (highlightedArticleId && highlightedArticleId !== article.id) {
                                    clearHighlight(highlightedArticleId);
                                  }
                                  const element = document.getElementById(`article-${article.id}`);
                                  if (element) {
                                    // Update hash
                                    window.location.hash = `article-${article.id}`;
                                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    // Highlight the new article
                                    setTimeout(() => {
                                      const innerBlock = element.querySelector('.bg-white.rounded-lg.shadow-sm') as HTMLElement;
                                      const targetElement = innerBlock || element;
                                      targetElement.classList.add('highlight-target');
                                      setHighlightedArticleId(article.id);
                                    }, 100);
                                  }
                                }}
                                style={{
                                  color: '#3B82F6',
                                  textDecoration: 'none',
                                  fontSize: '14px',
                                  lineHeight: '1.5',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                              >
                                {index + 1}. {article.title}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Articles - Each in its own block */}
                      {(() => {
                        const articlesLength = currentSection.articles.length;
                        return currentSection.articles.map((article, articleIndex) => {
                        
                        return (
                          <div 
                            key={article.id} 
                            id={`article-${article.id}`}
                            style={{ 
                              marginBottom: articleIndex < articlesLength - 1 ? '48px' : '0',
                              position: 'relative'
                            }}
                          >
                            {/* Article Block with Left Blue Border and Copy Button */}
                            <div 
                              className="bg-white rounded-lg shadow-sm"
                              style={{ 
                                borderLeft: '4px solid #00AEEF',
                                padding: '32px 40px',
                                borderRadius: '8px',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                                backgroundColor: '#FFFFFF',
                                position: 'relative'
                              }}
                            >
                              
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: '12px',
                                  marginBottom: '16px',
                                }}
                              >
                                <h2
                                  className="text-2xl font-bold text-gray-900"
                                  style={{
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    margin: 0,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {article.title}
                                </h2>

                                <button
                                  onClick={() => copySectionLink(`article-${article.id}`)}
                                  title="Copy link to this article"
                                  aria-label="Copy article link"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: copiedIds.has(`article-${article.id}`)
                                      ? 'rgba(16, 185, 129, 0.12)'
                                      : 'transparent',
                                    color: copiedIds.has(`article-${article.id}`)
                                      ? '#10B981'
                                      : '#9CA3AF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    flexShrink: 0,
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!copiedIds.has(`article-${article.id}`)) {
                                      e.currentTarget.style.background = '#F3F4F6';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!copiedIds.has(`article-${article.id}`)) {
                                      e.currentTarget.style.background = 'transparent';
                                    }
                                  }}
                                >
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    {copiedIds.has(`article-${article.id}`) ? (
                                      <polyline points="20 6 9 17 4 12" />
                                    ) : (
                                      <>
                                        <rect x="9" y="9" width="13" height="13" rx="2" />
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                      </>
                                    )}
                                  </svg>
                                </button>
                              </div>
                              
                              {/* Article Content */}
                              <div 
                                ref={(el) => {
                                  if (el) contentRefs.current.set(article.id, el);
                                }}
                                className="help-content"
                                style={{ 
                                  lineHeight: '1.75',
                                  fontSize: '15px',
                                  color: '#374151'
                                }}
                                dangerouslySetInnerHTML={{ __html: processContent(article.content, article.id) }}
                              />
                            </div>
                          </div>
                          );
                        });
                      })()}
                    </div>
                  );
                })() : (
                  <div className="flex items-center justify-center" style={{ height: '100%' }}>
                    <p className="text-gray-500 text-lg">Select a section from the sidebar to view articles</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}